import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import type { VNode } from "preact";
import emitter from "@webcore/core/events/eventemitter";
import { Fade } from "@core/components/Transitions/Fade";
import { AppClipExitScreen } from "@core/components/Layout";
import { VideoRecorderUI } from "@core/components/Media";
import { useARExperience, AppState } from "@core/hooks/useARExperience";
import { useXRErrorGuidance } from "@core/hooks/useXRErrorGuidance";
import { EJC_SPLASH_LAYOUT_OFFSET_PX } from "@core/components/Splash/EjcSplash";
import useAnalytics from "@core/hooks/useAnalyticsV2";
import { useAnalyticsIdentity } from "@core/hooks/useAnalyticsIdentity";
import type { ModelData } from "@core/content/types";
import getUserDevice from "@core/utils/getUserDevice";

import { Header } from "./components/Header";
import { ARButton } from "./components/ARButton";
import { AROverlay } from "./components/AROverlay";
import { AppLoadingOverlay } from "./components/AppLoadingOverlay";
import { Onboarding } from "./components/Onboarding";
import { PermissionDeniedOverlay } from "./components/PermissionDeniedOverlay";

import { ContentData } from "./data/data";

import HeroBg from "./assets/images/hero-bg.webp";
import Lockup from "./assets/images/lockup.svg";

const GA_MEASUREMENT_ID = "G-N9WDL6WP4H";

export function App(): VNode {
  const { device } = getUserDevice();
  const isIOS = device === 'iOS' || device === 'AppClip';

  const { errorMessage, showCopyLinkGuidance } = useXRErrorGuidance();

  // Same userId/sessionId carried into the App Clip URL, so Safari + App Clip
  // events group into one journey in reporting.
  const { userId, sessionId } = useAnalyticsIdentity();

  // Put both ids on the GA config so the auto page_view carries them; the
  // wrapper below guarantees every custom event carries ej_session_id too
  // (user_id is auto-added from config by useAnalytics). user_id stays GA's
  // built-in field; the session id is a custom `ej_session_id` param to avoid
  // clashing with GA's own `ga_session_id`.
  const analyticsConfig = useMemo(
    () => ({ user_id: userId, ej_session_id: sessionId }),
    [userId, sessionId]
  );
  const { initAnalytics, sendEvent: gaSendEvent } = useAnalytics(analyticsConfig);

  const sendEvent = useCallback(
    (name: string, params: Record<string, unknown> = {}) =>
      gaSendEvent(name, { ej_session_id: sessionId, ...params }),
    [gaSendEvent, sessionId]
  );

  useEffect(() => {
    void initAnalytics(GA_MEASUREMENT_ID);
  }, [initAnalytics]);

  const {
    appState,
    selectedModel,
    modelReady,
    isPreparingRuntime,
    isPlaced,
    isRecording,
    setIsRecording,
    xrIsChecking,
    xrIsCompatible,
    showLanding,
    handleLaunchAR,
    handleExitAR,
  } = useARExperience({
    contentData: ContentData,
    onLaunchClick: (model) => {
      sendEvent("launch_ar_click", {
        content_id: model.id,
        content_name: model.displayName,
      });
    },
    onEnteringXR: (model) => {
      sendEvent("ar_session_start", {
        content_id: model?.id,
        content_name: model?.displayName,
      });
    },
    onExitingXR: ({ durationSeconds }, model) => {
      sendEvent("ar_session_exit", {
        content_id: model?.id,
        content_name: model?.displayName,
        duration_seconds: durationSeconds,
      });
    },
  });

  // Emit the right XR-not-supported error event when the device can't do AR
  // (mirrors ejc-world handleLaunchAR). The button is no longer disabled for
  // incompatible devices — clicking it surfaces this error screen instead.
  const handleLaunchARWithErrorCheck = useCallback((model: ModelData) => {
    if (!xrIsCompatible && !xrIsChecking) {
      // iOS has no navigator.xr but the real issue is the App Clip minimum version
      // — emit xr-error-not-supported so the notSupported message path fires.
      // For Android/desktop, check navigator.xr to distinguish "API missing entirely"
      // (Firefox etc → copy-link guidance) from "API present but AR unsupported".
      (emitter as any).emit((!navigator.xr && !isIOS) ? 'xr-error-undefined' : 'xr-error-not-supported');
      return;
    }
    handleLaunchAR(model);
  }, [xrIsCompatible, xrIsChecking, isIOS, handleLaunchAR]);

  // In-AR onboarding: shown once per AR session, before user starts placing.
  const [onboardingDone, setOnboardingDone] = useState(false);
  useEffect(() => {
    if (appState === AppState.LANDING) setOnboardingDone(false);
  }, [appState]);

  const inARSession = appState === AppState.AR_EXPERIENCE && modelReady;
  const showOnboarding = inARSession && !onboardingDone;

  // --- Analytics: state-change events ----------------------------------------

  // XR compatibility resolved once after the inline check completes.
  const xrCompatibilityReportedRef = useRef(false);
  useEffect(() => {
    if (xrIsChecking || xrCompatibilityReportedRef.current) return;
    xrCompatibilityReportedRef.current = true;
    sendEvent("xr_compatibility_resolved", {
      compatible: xrIsCompatible,
    });
  }, [xrIsChecking, xrIsCompatible, sendEvent]);

  // Model finished loading inside the AR session.
  const modelReadyReportedRef = useRef(false);
  useEffect(() => {
    if (!modelReady) {
      modelReadyReportedRef.current = false;
      return;
    }
    if (modelReadyReportedRef.current) return;
    modelReadyReportedRef.current = true;
    sendEvent("ar_model_ready", {
      content_id: selectedModel?.id,
      content_name: selectedModel?.displayName,
    });
  }, [modelReady, selectedModel, sendEvent]);

  // First placement of the panel in the world.
  const placementReportedRef = useRef(false);
  useEffect(() => {
    if (!inARSession) {
      placementReportedRef.current = false;
      return;
    }
    if (!isPlaced || placementReportedRef.current) return;
    placementReportedRef.current = true;
    sendEvent("ar_placed", {
      content_id: selectedModel?.id,
      content_name: selectedModel?.displayName,
    });
  }, [inARSession, isPlaced, selectedModel, sendEvent]);

  // Recording lifecycle.
  useEffect(() => {
    if (!isRecording) return;
    sendEvent("recording_start", {
      content_id: selectedModel?.id,
    });
  }, [isRecording, selectedModel, sendEvent]);

  // Permission / WebXR error surfaced to the user.
  const errorReportedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!errorMessage) {
      errorReportedRef.current = null;
      return;
    }
    if (errorReportedRef.current === errorMessage) return;
    errorReportedRef.current = errorMessage;
    sendEvent("xr_error_shown", {
      message: errorMessage,
    });
  }, [errorMessage, sendEvent]);

  // Lock body scrolling while the splash is mounted — matches EjcSplash. The
  // splash is intentionally taller than the viewport (see
  // EJC_SPLASH_LAYOUT_OFFSET_PX) so we don't want the extra height to surface
  // a scrollbar.
  useLayoutEffect(() => {
    if (!showLanding) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showLanding]);

  return (
    <>
      {showLanding && (
        <div
          className="relative w-full overflow-hidden bg-ap-dark text-white"
          style={{
            height: `calc(100dvh + ${EJC_SPLASH_LAYOUT_OFFSET_PX}px)`,
            maxHeight: `calc(100dvh + ${EJC_SPLASH_LAYOUT_OFFSET_PX}px)`,
            overscrollBehavior: "none",
          }}
        >
          {/* Cover image + dark overlay — matches EjcSplash's cover layer. */}
          <img
            src={HeroBg}
            alt=""
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover object-center z-0"
          />
          <div className="absolute inset-0 z-0 bg-black/20" />

          {/* Project-specific mustard header bar. */}
          <div className="absolute inset-x-0 top-0 z-20">
            <Header />
          </div>

          {/* Lockup — sits in the heading slot between header and launch button. */}
          <div
            className="absolute inset-x-0 z-10 flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 pt-[80px] sm:pt-0"
            style={{
              top: "80px",
              bottom: `${130 + EJC_SPLASH_LAYOUT_OFFSET_PX}px`,
            }}
          >
            <img
              src={Lockup}
              alt="Mama Joys Walk — Come curious, leave changed"
              className="w-full max-w-[340px] sm:max-w-[520px] md:max-w-[580px] 2xl:max-w-[614px] h-auto"
            />
          </div>

          {/* Launch button — same slot/position as EjcSplash. */}
          <div className="max-w-[298px] w-full absolute z-20 left-1/2 -translate-x-1/2 pb-10 md:pb-[80px] bottom-[200px] md:bottom-[170px]">
            <ARButton
              model={ContentData[0]}
              onLaunchAR={handleLaunchARWithErrorCheck}
              isChecking={xrIsChecking}
              isCompatible={xrIsCompatible}
              runtimeIsPreparing={isPreparingRuntime}
            />
          </div>
        </div>
      )}

      <PermissionDeniedOverlay
        errorMessage={errorMessage || ""}
        visible={!!errorMessage}
        showCopyLinkGuidance={showCopyLinkGuidance}
      />

      {!errorMessage &&
        (appState === AppState.TRANSITIONING_XR ||
          (appState === AppState.AR_EXPERIENCE && !modelReady)) && (
          <div className="fixed inset-0 z-[60]">
            <AppLoadingOverlay
              message={
                appState === AppState.AR_EXPERIENCE
                  ? "Loading experience..."
                  : "Starting AR..."
              }
            />
          </div>
        )}

      {showOnboarding && (
        <Onboarding
          onContinue={() => {
            sendEvent("onboarding_continue", {
              content_id: selectedModel?.id,
            });
          }}
          onComplete={() => {
            sendEvent("onboarding_complete", {
              content_id: selectedModel?.id,
            });
            setOnboardingDone(true);
            (emitter as any).emit("begin-placement");
          }}
        />
      )}

      <Fade visible={inARSession && onboardingDone} duration={300}>
        <AROverlay onExit={handleExitAR} isRecording={isRecording} />
      </Fade>

      <Fade
        visible={inARSession && onboardingDone && isPlaced}
        duration={300}
      >
        <VideoRecorderUI
          buttonPosition="bottom-center"
          shrink
          shareTitle={selectedModel?.displayName || "Mama Joys Walk"}
          onRecordingStart={() => setIsRecording(true)}
          onRecordingStop={() => {
            setIsRecording(false);
            sendEvent("recording_stop", {
              content_id: selectedModel?.id,
            });
          }}
          onPreviewClose={() => {
            setIsRecording(false);
            sendEvent("recording_preview_close", {
              content_id: selectedModel?.id,
            });
          }}
          onError={(error) => {
            setIsRecording(false);
            sendEvent("recording_error", {
              content_id: selectedModel?.id,
              message:
                error instanceof Error ? error.message : String(error ?? ""),
            });
          }}
        />
      </Fade>

      {appState === AppState.APP_CLIP_EXITED && <AppClipExitScreen />}
    </>
  );
}
